import { inject, Injectable } from '@angular/core';
import { NotificationService } from '@core/services/notification/notification.service';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { PermissionNode } from '@shared/components/permissions-tree/models/permission-node.model';
import { PermissionNodeTransformerService } from '@shared/components/permissions-tree/services/permission-node-transformer.service';
import { catchError, of, tap } from 'rxjs';
import { PermissionsTreeStateModel } from './models/permissions-tree-state.model';
import {
  LoadPermissionNodesFromCollaborator,
  LoadPermissionNodesFromTemplate,
  LoadPermissionNodesFromUserPreset,
  SaveCollaboratorPermissionTree,
  SaveCollaboratorPermissionTreeFailure,
  SaveCollaboratorPermissionTreeSuccess,
  SaveUserPermissionPreset,
  SetCurrentTemplateId,
  SetHasUserPreset,
  SetSaveAsDefault,
  UpdatePermissionNodes,
} from './permissions-tree.actions';
import { PermissionsTreeService } from './services/permissions-tree.service';

const defaults: PermissionsTreeStateModel = {
  permissionNodes: [],
  currentTemplateId: null,
  hasUserPreset: false,
  saveAsDefault: false,
};

@State<PermissionsTreeStateModel>({
  name: 'permissionsTree',
  defaults,
})
@Injectable()
export class PermissionsTreeState {
  private readonly permissionsTreeService = inject(PermissionsTreeService);
  private readonly notificationService = inject(NotificationService);

  @Selector()
  static permissionNodes(state: PermissionsTreeStateModel): PermissionNode[] {
    return state.permissionNodes;
  }

  @Selector()
  static hierarchicalPermissionNodes(state: PermissionsTreeStateModel): PermissionNode[] {
    // Use static instance to avoid creating new instances on every call
    return PermissionsTreeState._getTransformerService().buildHierarchy(state.permissionNodes);
  }

  private static _transformerServiceInstance: PermissionNodeTransformerService | null = null;

  private static _getTransformerService(): PermissionNodeTransformerService {
    if (!PermissionsTreeState._transformerServiceInstance) {
      PermissionsTreeState._transformerServiceInstance = new PermissionNodeTransformerService();
    }
    return PermissionsTreeState._transformerServiceInstance;
  }

  @Selector()
  static currentTemplateId(state: PermissionsTreeStateModel): string | null {
    return state.currentTemplateId;
  }

  @Selector()
  static hasUserPreset(state: PermissionsTreeStateModel): boolean {
    return state.hasUserPreset;
  }

  @Selector()
  static saveAsDefault(state: PermissionsTreeStateModel): boolean {
    return state.saveAsDefault;
  }

  @Action(LoadPermissionNodesFromTemplate)
  loadPermissionNodesFromTemplate(
    { patchState }: StateContext<PermissionsTreeStateModel>,
    { serviceTypes, controlTowerId }: LoadPermissionNodesFromTemplate
  ) {
    patchState({
      permissionNodes: [],
      currentTemplateId: null,
    });
    return this.permissionsTreeService.getPermissionTemplateByServiceTypes(serviceTypes, controlTowerId).pipe(
      tap(nodes => {
        patchState({
          permissionNodes: nodes,
          currentTemplateId: nodes[0]._id,
        });
      }),
      catchError(error => {
        console.error(error);
        //TODO: display the error in the UI
        return of(null);
      })
    );
  }

  @Action(LoadPermissionNodesFromCollaborator)
  loadPermissionNodesFromCollaborator(
    { patchState }: StateContext<PermissionsTreeStateModel>,
    { collaboratorId, controlTowerId }: LoadPermissionNodesFromCollaborator
  ) {
    patchState({
      permissionNodes: [],
      currentTemplateId: null,
    });

    return this.permissionsTreeService.getPermissionTree(collaboratorId, controlTowerId).pipe(
      tap(nodes => {
        patchState({
          permissionNodes: nodes,
          currentTemplateId: null, // Not template-based
        });
      }),
      catchError(error => {
        console.error(error);
        //TODO: display the error in the UI
        return of(null);
      })
    );
  }

  @Action(SaveCollaboratorPermissionTree)
  saveCollaboratorPermissionTree(
    { dispatch }: StateContext<PermissionsTreeStateModel>,
    { collaboratorId, newTree }: SaveCollaboratorPermissionTree
  ) {
    return this.permissionsTreeService.saveCollaboratorPermissionTree(collaboratorId, newTree).pipe(
      tap(rootNodeIds => {
        dispatch(new SaveCollaboratorPermissionTreeSuccess(rootNodeIds));
      }),
      catchError(error => {
        console.error('Error saving collaborator permission tree:', error);
        dispatch(new SaveCollaboratorPermissionTreeFailure(error.message || 'Failed to save permission tree'));
        return of(null);
      })
    );
  }

  @Action(SaveCollaboratorPermissionTreeSuccess)
  saveCollaboratorPermissionTreeSuccess(
    _ctx: StateContext<PermissionsTreeStateModel>,
    { rootNodeIds }: SaveCollaboratorPermissionTreeSuccess
  ) {
    this.notificationService.showSuccess('Permissions Updated Successfully');
  }

  @Action(SaveCollaboratorPermissionTreeFailure)
  saveCollaboratorPermissionTreeFailure(
    _ctx: StateContext<PermissionsTreeStateModel>,
    { error }: SaveCollaboratorPermissionTreeFailure
  ) {
    this.notificationService.showError('Failed to update permissions');
  }

  @Action(UpdatePermissionNodes)
  updatePermissionNodes({ patchState }: StateContext<PermissionsTreeStateModel>, { nodes }: UpdatePermissionNodes) {
    patchState({
      permissionNodes: nodes,
    });
  }

  @Action(SetCurrentTemplateId)
  setCurrentTemplateId({ patchState }: StateContext<PermissionsTreeStateModel>, { templateId }: SetCurrentTemplateId) {
    patchState({
      currentTemplateId: templateId,
    });
  }

  @Action(LoadPermissionNodesFromUserPreset)
  loadPermissionNodesFromUserPreset(
    { patchState }: StateContext<PermissionsTreeStateModel>,
    { inviteeIdentifier, controlTowerId }: LoadPermissionNodesFromUserPreset
  ) {
    return this.permissionsTreeService.getUserPermissionPreset(inviteeIdentifier, controlTowerId).pipe(
      tap(nodes => {
        if (nodes && nodes.length > 0) {
          patchState({
            permissionNodes: nodes,
            hasUserPreset: true,
            currentTemplateId: null,
          });
        } else {
          patchState({ hasUserPreset: false });
        }
      }),
      catchError(error => {
        console.error('Error loading user permission preset:', error);
        patchState({ hasUserPreset: false });
        return of(null);
      })
    );
  }

  @Action(SaveUserPermissionPreset)
  saveUserPermissionPreset(
    _ctx: StateContext<PermissionsTreeStateModel>,
    { inviteeIdentifier, inviteeType, permissionNodes }: SaveUserPermissionPreset
  ) {
    return this.permissionsTreeService.saveUserPermissionPreset(inviteeIdentifier, inviteeType, permissionNodes).pipe(
      catchError(error => {
        console.error('Error saving user permission preset:', error);
        return of(null);
      })
    );
  }

  @Action(SetSaveAsDefault)
  setSaveAsDefault({ patchState }: StateContext<PermissionsTreeStateModel>, { value }: SetSaveAsDefault) {
    patchState({ saveAsDefault: value });
  }

  @Action(SetHasUserPreset)
  setHasUserPreset({ patchState }: StateContext<PermissionsTreeStateModel>, { value }: SetHasUserPreset) {
    patchState({ hasUserPreset: value });
  }
}
